import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  alpha,
  useTheme,
  Button,
  Autocomplete,
  Checkbox,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import moment, { Moment } from 'moment';
import type { batchDetail, DashboardFilters } from '@/types/dashboard';
import { SCHOOL_ID_LENGTH } from '@/constants/schoolConstants';

const CHECKBOX_ICON = <CheckBoxOutlineBlankIcon fontSize="small" />;
const CHECKBOX_CHECKED_ICON = <CheckBoxIcon fontSize="small" />;
const SELECT_ALL_ID = '__select_all__';
const SELECT_ALL_OPTION: batchDetail = {
  id: SELECT_ALL_ID,
  name: 'Select All',
  displayName: 'Select All',
};

interface FilterPanelProps {
  filters: DashboardFilters;
  onChange: (updated: Partial<DashboardFilters>) => void;
  selectedBatchCount: number;
  totalBatchCount: number;
  getSchoolData: ()=>void;
  isBatchesLoading?: boolean;
  batchesError?: string;
}

const LABEL_SX = {
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.6px',
  textTransform: 'uppercase' as const,
  color: 'text.disabled',
  mb: 0.5,
};

/**
 * Filter panel card — School ID search, Batch dropdown,
 * Exam Date & Time display, and Status dropdown.
 */
export default function FilterPanel({
  filters,
  onChange,
  selectedBatchCount,
  totalBatchCount,
  getSchoolData,
  isBatchesLoading = false,
  batchesError = '',
}: FilterPanelProps) {
  const theme = useTheme();
  const batchList = filters?.batches || [];
  const selectedBatchIds = filters?.selectedBatches || [];

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${alpha(theme.palette.divider, 1)}`,
        borderRadius: '12px',
        p: 2.5,
        mb: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} gap={3} alignItems="flex-start" sx={{width:'100%'}}>
        {/* School ID */}
        <Box sx={{ flex: '0 0 auto', minWidth: 280 }}>
          <Typography sx={LABEL_SX}>School ID</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              id="school-id-filter"
              size="small"
              value={filters.schoolId}
              onChange={(e) => onChange({ schoolId: e.target.value })}
              slotProps={{
                htmlInput: {
                  maxLength: SCHOOL_ID_LENGTH,
                },
              }}
              placeholder="Enter School ID"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 260, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={getSchoolData}
              sx={{ fontWeight: 700, px: 2, height: 40 }}
              disabled={filters.schoolId.trim().length != SCHOOL_ID_LENGTH}
            >
              Get Data
            </Button>
          </Stack>
        </Box>

        {/* Batch multiselect search */}
        <Box sx={{ flex: '0 0 auto', width: 300 }}>
        <Typography sx={LABEL_SX}>Batch</Typography>
          <Autocomplete
            multiple
            disableCloseOnSelect
            limitTags={1}
            renderTags={(value) =>
              value.length ? [
                <Chip
                  key="summary"
                  label={
                    value.length === 1
                      ? value[0].displayName
                      : `${value[0].displayName} +${value.length - 1}`
                  }
                  size="small"
                />
              ] : null
            }
            id="batch-filter"
            options={batchList.length > 0 ? [SELECT_ALL_OPTION, ...batchList] : batchList}
            value={batchList.filter((batch) => selectedBatchIds.includes(batch.id))}
            onChange={(_event, newValue, _reason, details) => {
              const optionId = details?.option?.id;

              if (optionId === SELECT_ALL_ID) {
                // Toggle: if everything is already selected, clear; otherwise select all.
                const allSelected = selectedBatchIds.length === batchList.length;
                onChange({ selectedBatches: allSelected ? [] : batchList.map((batch) => batch.id) });
                return;
              }

              onChange({
                selectedBatches: newValue
                  .filter((batch) => batch.id !== SELECT_ALL_ID)
                  .map((batch) => batch.id),
              });
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.displayName}
            loading={isBatchesLoading}
            noOptionsText={isBatchesLoading ? 'Loading batches…' : 'No batches available'}
            disabled={filters?.schoolId.length!=SCHOOL_ID_LENGTH}
            renderOption={(props, option, { selected }) => {
              const isSelectAll = option.id === SELECT_ALL_ID;
              const isChecked = isSelectAll
                ? batchList.length > 0 && selectedBatchIds.length === batchList.length
                : selected;

              return (
                <li {...props} key={option.id}>
                  <Checkbox
                    icon={CHECKBOX_ICON}
                    checkedIcon={CHECKBOX_CHECKED_ICON}
                    checked={isChecked}
                    size="small"
                    sx={{ mr: 1, p: 0.5 }}
                  />
                  {option.displayName}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder={
                (filters?.selectedBatches?.length ?? 0) === 0
                  ? "Select batches"
                  : ""
              } />
            )}
            sx={{
              '& .MuiOutlinedInput-root': {
                maxHeight: 40,
                borderRadius: '8px',
                py: 0.25,
              },
            }}
          />
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.5, color: 'primary.main', fontWeight: 600 }}
        >
          {selectedBatchCount} / {totalBatchCount} Batches Selected
        </Typography>
        {batchesError && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.5, color: 'error.main' }}
          >
            {batchesError}
          </Typography>
        )}
      </Box>

        {/* Exam Date & Time */}
        <Box sx={{ flex: '0 0 auto', minWidth: 220 }}>
          <Typography sx={LABEL_SX}>Exam Date &amp; Time</Typography>
          <DateTimePicker
            disabled={filters?.schoolId.length==0 || filters?.selectedBatches?.length==0}
            value={filters.examDate ? moment(filters.examDate) : null}
            onChange={(value: Moment | null) =>
              onChange({ examDate: value && value.isValid() ? value.toISOString() : '' })
            }
            slotProps={{ textField: { size: 'small', sx: { width: 220 } } }}
            label="Select date & time"
          />
        </Box>
      </Stack>
    </Paper>
  );
}
